// The workspace's groups follow the machine's (workspaceSync.ts): this hears the machine's state (the layout event once
// caps says `layout`, and the layout op on each connection, whose "unknown op" means an agent from before the machine
// kept them: the groups are then this browser's alone, as they were) and sends what changes here (layout.put), one
// put at a time, each from what was last heard. The first state heard takes this browser's old workspaces in when the
// machine has none yet (seed). Zoom is the machine's own (a put keeps it): a group unzoomed here (its Zoomed clicked,
// or a pane added or taken away) is unzoomed there with tmux's own command once its put is in. Each state heard is kept
// in this browser too (keepArrangement): the next visit shows it until the machine says it again.

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { conn } from '../net/connection';
import type { MachineLayout } from '../net/protocol';
import { setLayoutHandler } from '../term/manager';
import { store } from '../store';
import { current, keepArrangement, shownIn, type Saved, type TabsState } from './tabs';
import { adopt, changes, machineLayout, merge, seedPut, shape, type LayoutPut } from './workspaceSync';

/** waiting: nothing heard yet; machine: the groups are the machine's; local: an agent that doesn't keep them. */
export type SyncMode = 'waiting' | 'machine' | 'local';

type PutAnswer = { applied?: number[]; stale?: number[]; layout: MachineLayout };

/** The machine's state as last heard (tests read it: window.__cmx.layout). */
let lastHeard: MachineLayout | null = null;
export const heardLayout = () => lastHeard;

/** Notes the machine's state as heard, and keeps its arrangement for the next visit. */
function noteHeard(l: MachineLayout) {
  lastHeard = l;
  keepArrangement(store.get().me?.machine?.id, l.groups);
}

/**
 * Follows the machine's workspaces for the state `tabs` (set with `setTabs`), loaded as `saved`. Returns the mode and
 * `fromMachine`: the last state the machine's news made, so a render can tell a change that came from there (it
 * animates, and doesn't take the focus unless the machine moved it) from one made here.
 */
export function useWorkspaceSync(saved: Saved, tabs: TabsState, setTabs: Dispatch<SetStateAction<TabsState>>) {
  const [mode, setMode] = useState<SyncMode>('waiting');
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const heard = useRef<MachineLayout | null>(null);
  /** A connection just started: the next state heard is taken whatever its version (a machine that lost its own). */
  const fresh = useRef(true);
  const seeding = useRef(false);
  /** The put on the way: the shape of each group it sent. */
  const inflight = useRef<Map<number, string> | null>(null);
  /** Groups unzoomed here that the machine still says are zoomed: shown unzoomed until it says so too. */
  const unzooming = useRef(new Set<number>());
  const fromMachine = useRef<TabsState | null>(null);
  /** An updater making the state from the machine's news, noted as such; one result for the same state (React may
   *  call an updater twice), so the state shown is the one noted. */
  const fromMachineBy = (f: (t: TabsState) => TabsState) => {
    let last: [TabsState, TabsState] | null = null;
    return (t: TabsState) => {
      if (last?.[0] !== t) last = [t, stillUnzoomed(f(t))];
      return (fromMachine.current = last[1]);
    };
  };
  const stillUnzoomed = (t: TabsState): TabsState =>
    t.groups.some((g) => g.zoomed && unzooming.current.has(g.id)) ? { ...t, groups: t.groups.map((g) => (g.zoomed && unzooming.current.has(g.id) ? { ...g, zoomed: false } : g)) } : t;
  // a put's answer that changes nothing here still lets the next put go: flushing waits for a render
  const [settled, setSettled] = useState(0);

  useEffect(() => {
    const hear = (heardNow: MachineLayout, answered = false) => {
      const next = machineLayout(heardNow);
      const was = heard.current;
      if (seeding.current || (was && !fresh.current && next.version < was.version)) return;
      fresh.current = false;
      heard.current = next;
      noteHeard(next);
      for (const id of unzooming.current) if (!next.groups.some((g) => g.id === id && g.zoomed)) unzooming.current.delete(id);
      if (modeRef.current !== 'machine') {
        // the first state: the groups here were this browser's own (or none), so the row shown is found by its lead
        modeRef.current = 'machine';
        setMode('machine');
        const own = tabsRef.current;
        const lead = saved.machine ? saved.lead : (current(own)?.lead ?? null);
        const seed = next.seeded || saved.machine ? null : seedPut(own);
        if (seed) return void sendSeed(seed, own.page, lead);
        setTabs(fromMachineBy((t) => adopt(next, t.page, saved.machine ? saved.tabs.at : null, lead)));
        return;
      }
      const sent = inflight.current ?? undefined;
      setTabs(fromMachineBy((t) => merge(t, was, next, sent, answered)));
      setSettled((n) => n + 1);
    };
    /** This browser's old workspaces go to a machine that has none; the row shown is found again by its lead. */
    const sendSeed = (seed: LayoutPut, page: TabsState['page'], lead: number | null) => {
      seeding.current = true;
      conn
        .control<PutAnswer>('layout.put', seed)
        .then((r) => r.layout)
        .catch(() => conn.control<MachineLayout>('layout'))
        .then(
          (answer) => {
            const l = machineLayout(answer);
            seeding.current = false;
            heard.current = l;
            noteHeard(l);
            setTabs(fromMachineBy((t) => adopt(l, t.page ?? page, null, lead)));
          },
          () => {
            // not connected: the next connection's state seeds again
            seeding.current = false;
            modeRef.current = 'waiting';
            setMode('waiting');
          },
        );
    };
    setLayoutHandler({
      layout: (l) => hear(l),
      ready() {
        fresh.current = true;
        // the agent's state, and whether it keeps one at all
        conn.control<MachineLayout>('layout').then(
          (l) => hear(l),
          (e: Error) => {
            if (e.message !== 'unknown op' || modeRef.current === 'machine') return;
            modeRef.current = 'local';
            setMode('local');
          },
        );
      },
    });
    return () => setLayoutHandler(null);
    // saved is what the page loaded, once
  }, []);

  // what changed here goes to the machine once rendered (so it's read from what's shown), one put at a time
  useEffect(() => {
    if (mode !== 'machine' || !heard.current || inflight.current || seeding.current || !conn.connected) return;
    const put = changes(tabs, heard.current);
    if (!put) return void unzoom(tabs, heard.current);
    const sent = new Map(put.groups.map((g) => [g.id, shape(g)]));
    inflight.current = sent;
    conn.control<PutAnswer>('layout.put', put).then(
      (r) => {
        const before = heard.current;
        const next = r.layout && machineLayout(r.layout);
        if (next && (!before || next.version >= before.version)) {
          heard.current = next;
          noteHeard(next);
          setTabs(fromMachineBy((t) => merge(t, before, next, sent, true)));
        }
        inflight.current = null;
        setSettled((n) => n + 1);
      },
      () => {
        // not connected: the next state heard (the next connection's) has it sent again
        inflight.current = null;
      },
    );
  }, [mode, tabs, settled, setTabs]);

  /** A group unzoomed here and zoomed there: tmux's resize-pane -Z on its focused pane (it toggles), one at a time, as
   *  a put (it moves the group's ver on). */
  const unzoom = (t: TabsState, l: MachineLayout) => {
    const g = t.groups.find((x) => !x.zoomed && l.groups.some((h) => h.id === x.id && h.zoomed) && !unzooming.current.has(x.id));
    const pane = g && (shownIn(g, g.focused) ?? g.panes.flatMap((p) => p.tabs).find((id) => id > 0));
    if (!g || pane === undefined) return;
    unzooming.current.add(g.id);
    inflight.current = new Map();
    const done = () => {
      inflight.current = null;
      setSettled((n) => n + 1);
    };
    // not taken (not connected, an agent without tmux): it stays unzoomed here, and isn't asked again
    conn.control('tmux', { args: ['resize-pane', '-Z', '-t', `%${pane}`] }).then(done, done);
  };

  return { mode, fromMachine };
}
